<template>
    <cis-head
        :title="$page.props.title"
    />
    <v-app
        id="root"
    >
        <notifications position="top right"/>
        <app-bar
            :menu="menu"
            :menu-model="menuModel"
            :active-menu="activeMenu"
            :has-background="(route().current('homepage'))"
        >
        </app-bar>
        <v-main id="main" name="main">
            <client-only>
                <v-divider v-if="!(route().current('homepage'))"></v-divider>
            </client-only>
            <breadcrumb v-if="!group?.id && !$page.props.globalBreadcrumbOverride" :items="$page.props.breadcrumb"></breadcrumb>
            <slot></slot>
        </v-main>

        <logo-marquee v-if="!(route().current('garland.partners'))"></logo-marquee>
        <v-footer v-if="!(route().current('garland.partners'))" class="bg-green-darken-4 flex-grow-0 flex-wrap" name="app-footer">

            <v-container class="flex-1-1-100">
                <v-row class="pt-8 px-4">
                    <v-col cols="12" md="4" class="d-flex flex-column align-center align-md-start ga-3">
                        <div v-if="contact?.email" class="d-flex align-center ga-1 text-body-2">
                            <v-icon :icon="mdiEmailOutline" size="16px"></v-icon>
                            <span>{{ contact.email }}</span>
                        </div>
                        <div v-if="contact?.phone" class="d-flex align-center ga-1 text-body-2">
                            <v-icon :icon="mdiCellphone" size="16px"></v-icon>
                            <span>{{ contact.phone }}</span>
                        </div>
                        <div v-if="contact?.openingHours" class="d-flex align-center ga-1 text-body-2">
                            <v-icon :icon="mdiClock" size="16px"></v-icon>
                            <span>{{ contact.openingHours }}</span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="4" class="text-center d-flex flex-column align-center ga-4 my-6 mt-md-3">
                        <v-img src="../../../../public/logo_inverted.svg" width="267"></v-img>
                        <p class="text-body-2">{{$t('basic-layout.motto')}}</p>
                    </v-col>
                    <v-col cols="12" md="4" class="text-center text-md-right d-flex text-body-2 flex-column ga-3">
                        <Link v-for="item in footerMenu" :key="item.url" :href="item.route ? route(item.route) : item.url" class="text-white" >{{item.title}}</Link>
                    </v-col>
                    <v-col cols="12">
                        <v-divider/>
                    </v-col>
                    <v-col cols="12" class="text-white text-center pt-0 text-subtitle-2">
                        <span class="mr-2">{{ $t('basic-layout.all_rights_reserved')}} – {{ year }} – {{$t('basic-layout.company')}}</span>
                        <v-btn
                            class="text-none px-0 text-subtitle-2 pb-1"
                            variant="plain"
                            color="white"
                            @click="showModalCookie"
                        >
                            {{ $t('basic-layout.cookies_settings')}}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
            <div id="footer-teleport" class="flex-1-1-100"></div>
        </v-footer>

        <component v-if="$page.props.user?.token" :is="liveEdit"></component>
        <newsletter-el v-if="showNewsletter" :show="showNewsletter" v-bind="newsletterProps" @close="showNewsletter = false"></newsletter-el>
    </v-app>
</template>

<script setup>
import { computed, defineAsyncComponent, inject, markRaw, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import {usePage, Link, router} from "@inertiajs/vue3";
import CisHead from "../components/CisHead.vue";
import axios from "axios";
import AppBar from "../components/AppBar.vue";
import ClientOnly from "../components/ClientOnly.vue";
import {useNotify} from "../../../../Modules/Admin/Resources/assets/js/composables/notify";
import Breadcrumb from "../components/Breadcrumb.vue";
import {useCartStore} from "../../modules/eshop/store/cart";
import pinia from "../../plugins/pinia";
import { mdiCellphone, mdiClock, mdiEmailOutline } from '@mdi/js';
import LogoMarquee from '../components/footer/LogoMarquee.vue';

useNotify();
const route = inject('route');

const liveEdit = defineAsyncComponent(() => import('./../components/LiveEdit.vue'));
const newsletterEl = defineAsyncComponent(() => import('./../components/Newsletter.vue'));

const footerMenu = computed(() => usePage().props.menu?.filter(el => el.position === 'footer'))
const partnersMenu = computed(() => usePage().props.menu?.filter(el => el.position === 'partners'))
const showModalCookie = async () => {
    const script = document.getElementById('cookieScript');
    const modal = document.getElementById('modal-cookie');
    if (!script) {
        const {data} = await axios.get('/get-cookie-settings');
        document.body.insertAdjacentHTML('beforeend', data.html);
        document.head.append(document.createRange().createContextualFragment(data.script));
        window?.laravelCookieConsent.initButtons();
        window?.laravelCookieConsent.modalShow();
        return;
    }
    if (!modal) {
        const response = await fetch('/get-cookie-modal');
        const data = await response.json();
        document.body.insertAdjacentHTML('beforeend', data.html);
        window?.laravelCookieConsent.initButtons();
        window?.laravelCookieConsent.modalShow();
    }
}

const user = computed(() => usePage().props.customer);
const menu = computed(() => usePage().props.menuGroups);

const group = computed(() => usePage().props.group);
const contact = computed(() => usePage().props.contact);

const menuModel = ref(group.value?.path_ids || []);

watch(() => group.value?.path_ids, (val) => {
    menuModel.value = val;
})

const activeMenu = computed(() => {
    const active = {};
    if (group.value) {
        for (let i = 0; i < menu.value.length; i++) {
            if (group.value.path_ids.includes(menu.value[i].id)) {
                active[menu.value[i].id] = true;
            }
            if (menu.value[i].children) {
                for (let j = 0; j < menu.value[i].children.length; j++) {
                    if (group.value.path_ids.includes(menu.value[i].children[j].id)) {
                        active[menu.value[i].children[j].id] = true;
                        return active;
                    }
                }
            }
        }
    }
    return active;
})

const events = markRaw(['mousemove', 'touchmove', 'click', 'scroll', 'resize']);

axios.interceptors.request.use(
    config => {
        const token = user.value?.token;
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        return config;
    },
    error => {
        return Promise.reject(error)
    });
let first = true;
if (!import.meta.env.SSR) {
    nextTick(() => {
        if (!import.meta.env.SSR) {
            document.body.classList.remove('d-none')
        }
    })
    router.on('navigate', () => {
        if (!first) {
            window.resetDL();
        }
        first = false;
    })
}

const year = ref((new Date()).getFullYear());

const showNewsletter = ref(false);
const newsletterProps = ref({});

let timeout;

const newsletter = () => {
    if (!import.meta.env.SSR) {
        const handler = () => {
            clearTimeout(timeout);
            events.forEach(function (e) {
                window.removeEventListener(e, handler);
            });
            timeout = setTimeout(() => {
                axios.get(route('newsletter.modal')).then(resp => {
                    const {data} = resp;
                    newsletterProps.value = {
                        text: data.text || '',
                        image: data.image || null,
                    }
                    if ('show' in data) {
                        showNewsletter.value = true;
                    }
                })
            }, 5000)
        }
        events.forEach(function (e) {
            window.addEventListener(e, handler, {once: true});
        });
    }
}

/*onMounted(() => {
    newsletter();
})

onUnmounted(() => {
    clearTimeout(timeout);
})*/

</script>

<style lang="scss">

</style>

<style scoped lang="scss">
:deep(.c-mobile-menu) {
    a {
        color: currentColor;
    }

    .v-list-item-title {
        &:first-child {
            display: none;
        }

        &:last-child {
            display: block;
        }
    }
}
</style>
